import { Component, OnInit } from '@angular/core';
import {
  GuardsCheckEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { select, Store } from '@ngxs/store';
import { AppActions, FetchFeatureflags, OnderwijsLocatiesActions, SchooladviezenActions } from '@store/actions';
import { DeelnemersSelectors, UiSelectors } from '@store/selectors';
import { AppVariants } from '@store/models/generic.model';
import { GenericSelectors } from '@store/selectors/generic.selectors';
import { StateService } from '@services/state.service';
import { AuthService } from '@services/auth.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'cito-doe-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  forbidden403Access$: Observable<boolean> = this.store.select(UiSelectors.forbidden403Access);
  printStartcodes$: Observable<boolean> = this.store.select(DeelnemersSelectors.printStartcodes);
  appVariant = select(GenericSelectors.AppVariant);

  readonly loading = toSignal(
    this.router.events.pipe(
      filter(
        (e) =>
          e instanceof NavigationStart ||
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError,
      ),
      map((e) => e instanceof NavigationStart),
      debounceTime(200),
    ),
    { initialValue: false },
  );
  notAuthorized$: Observable<boolean> = of(false);
  captureMode = environment.captureMode || false;

  constructor(
    private readonly stateService: StateService,
    private readonly authService: AuthService,
    private router: Router,
    private store: Store,
  ) {
    this.authService
      .isAuthenticated()
      .pipe(
        takeUntilDestroyed(),
        filter((isAuthenticated) => isAuthenticated),
        take(1),
        switchMap(() => {
          this.stateService.initState();
          this.store.dispatch(new FetchFeatureflags());
          return this.authService.getAccessToken();
        }),
        filter(Boolean),
      )
      .subscribe((token) => {
        const payload = this.parseJWT(token);
        const roles = Array.isArray(payload.role)
          ? payload.role
          : payload.role
          ? [payload.role]
          : [];

        this.store.dispatch(
          new AppActions.setUserInfo({
            name: payload.name || '',
            roles: roles,
            email: payload.email || '',
          }),
        );
        this.store.dispatch(new OnderwijsLocatiesActions.LoadOnderwijsLocaties());
        this.store.dispatch(new SchooladviezenActions.ReadSchooladviezen());
      });
  }

  ngOnInit() {
    this.router.events.pipe(filter((e) => e instanceof GuardsCheckEnd)).subscribe((event) => {
      if (event instanceof GuardsCheckEnd) {
        this.notAuthorized$ = of(!event.shouldActivate);
      }
    });
  }

  logout() {
    this.authService.logoff();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Failed to parse JWT:', e);
      return {};
    }
  }

  protected readonly AppVariants = AppVariants;
}
